import { CartProduct } from '../../../shared/models/cart.model';
import { Product } from '../../../shared/models/product.model';

/** GA4 caps item-scoped category parameters at five slots. */
const CATEGORY_SLOTS = 5;

/**
 * One entry of the `items` array of a GA4 ecommerce event. `item_category` is the
 * item-scoped dimension Marketing groups "Conversion by category" on, so it must be
 * omitted rather than sent empty when a product carries no category — an empty string
 * would register as a real value and pollute the report.
 */
export interface Ga4Item {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_brand?: string;
  price: number;
  quantity: number;
  index?: number;
}

function assignCategories(item: Ga4Item, categoryNames: string[]): void {
  categoryNames
    .filter(name => !!name)
    .slice(0, CATEGORY_SLOTS)
    .forEach((name, index) => {
      const key = index === 0 ? 'item_category' : `item_category${index + 1}`;
      item[key] = name;
    });
}

export function toGa4ItemFromCartProduct(product: CartProduct): Ga4Item {
  const item: Ga4Item = {
    item_id: String(product.id),
    item_name: product.name,
    price: product.price,
    quantity: product.quantity,
  };

  const categoryNames = product.categories?.length
    ? product.categories.map(category => category.name)
    : [product.category];
  assignCategories(item, categoryNames);

  if (product.brand) item.item_brand = product.brand;

  return item;
}

export function toGa4ItemFromProduct(
  product: Product,
  quantity?: number
): Ga4Item {
  const item: Ga4Item = {
    item_id: String(product.id),
    item_name: product.name,
    price: product.price,
    quantity: quantity ?? product.quantity ?? 1,
  };

  assignCategories(
    item,
    (product.categories || []).map(category => category.name)
  );

  if (product.brand?.brandName) item.item_brand = product.brand.brandName;
  if (!item.quantity) item.quantity = 1;

  return item;
}

/**
 * Collapses the products of every resident cart in an order into GA4 line items.
 * An order spans several residents and, for a customer-wide order, several
 * departments, so the same article routinely appears many times; GA4 expects one
 * line item per `item_id` with the quantities summed.
 */
export function aggregateCartProductItems(
  products: Array<CartProduct>
): Ga4Item[] {
  const itemsById = new Map<string, Ga4Item>();

  (products || [])
    .filter(product => product?.quantity > 0)
    .forEach(product => {
      const item = toGa4ItemFromCartProduct(product);
      const existing = itemsById.get(item.item_id);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        itemsById.set(item.item_id, item);
      }
    });

  return Array.from(itemsById.values());
}
